// HubStore.jsx - Simple mode hub purchase store
// Players can buy additional hubs at major world airports

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { SIMPLE_HUBS } from "../data/SimpleHubs.js";
import { Button } from "@/components/ui/button";
// Icons removed - text-only UI

// Format price with commas
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

// Hub Card Component
function HubCard({ hub, isOwned, canAfford, onPurchase, t }) {
  const [isHovered, setIsHovered] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handlePurchaseClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    onPurchase(hub);
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: isHovered ? "#0f0f0f" : "#0a0a0a",
        border: `1px solid ${isOwned ? "#22c55e" : isHovered ? "#333" : "#1a1a1a"}`,
        borderRadius: "12px",
        padding: "20px",
        transition: "all 0.2s ease",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ color: "#22c55e", fontWeight: "bold", fontSize: "18px" }}>{hub.icao}</span>
            <span style={{ color: "#525252" }}>-</span>
            <span style={{ color: "#e5e5e5", fontSize: "16px" }}>{hub.city}</span>
          </div>
          <p style={{ color: "#737373", fontSize: "13px" }}>{hub.name}</p>
        </div>
        {isOwned && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            backgroundColor: "#22c55e20",
            padding: "4px 10px",
            borderRadius: "4px",
          }}>
            <span style={{ color: "#22c55e", fontSize: "12px", fontWeight: "bold" }}>{t('hubStore.owned')}</span>
          </div>
        )}
      </div>

      {/* Region Badge */}
      <div style={{ marginBottom: "16px" }}>
        <span style={{
          backgroundColor: "#1a1a1a",
          color: "#737373",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "11px",
          textTransform: "uppercase",
        }}>
          {hub.region}
        </span>
      </div>

      {/* Price and Button */}
      {!isOwned && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{
              color: canAfford ? "#e5e5e5" : "#737373",
              fontSize: "18px",
              fontWeight: "bold",
            }}>
              {formatPrice(hub.price)}
            </span>
          </div>

          {!showConfirm ? (
            <Button
              onClick={handlePurchaseClick}
              disabled={!canAfford}
              style={{
                backgroundColor: canAfford ? "#22c55e" : "#262626",
                color: canAfford ? "#000" : "#525252",
                border: "none",
                padding: "8px 20px",
                borderRadius: "6px",
                fontWeight: "bold",
                cursor: canAfford ? "pointer" : "not-allowed",
                opacity: canAfford ? 1 : 0.7,
              }}
            >
              {t('hubStore.purchase')}
            </Button>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                onClick={handleCancel}
                style={{
                  backgroundColor: "#262626",
                  color: "#a1a1a1",
                  border: "1px solid #333",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleConfirm}
                style={{
                  backgroundColor: "#22c55e",
                  color: "#000",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontWeight: "bold",
                  fontSize: "12px",
                }}
              >
                {t('common.confirm')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Can't afford message */}
      {!isOwned && !canAfford && (
        <p style={{ color: "#ef4444", fontSize: "11px", marginTop: "8px", textAlign: "right" }}>
          {t('hubStore.insufficientFunds')}
        </p>
      )}
    </div>
  );
}

export default function HubStore() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { worldState, purchaseHub, addEvent, updateWorldState } = useWorldState();

  // Mode check - only available in Simple mode
  const gameMode = worldState?.gameMode || 'simple';
  const isSimpleMode = gameMode === 'simple' || gameMode === 'arcade';

  const cash = worldState?.airline?.cash || 0;
  const ownedHubs = worldState?.ownedHubs || ['KPDX'];
  const pendingOffers = worldState?.pendingExclusiveOffers || [];

  const handlePurchase = useCallback((hub) => {
    purchaseHub(hub.icao, hub.city, hub.price);
    addEvent({
      type: "HUB_PURCHASED",
      severity: "success",
      message: `Welcome to ${hub.city}! ${hub.icao} is now your hub.`,
    });
  }, [purchaseHub, addEvent]);

  // Accept a pending exclusive offer
  const handleAcceptPendingOffer = useCallback((offer) => {
    const { hub, price } = offer;
    if (cash < price) return;

    // Update state: deduct cash, add hub, remove from pending
    updateWorldState(prev => ({
      ...prev,
      airline: {
        ...prev.airline,
        cash: prev.airline.cash - price,
        hubs: [...(prev.airline?.hubs || ['KPDX']), hub],
        completedHubAuctions: [...(prev.airline?.completedHubAuctions || []), hub],
      },
      ownedHubs: [...new Set([...(prev.ownedHubs || ['KPDX']), hub])],
      pendingExclusiveOffers: (prev.pendingExclusiveOffers || []).filter(o => o.hub !== hub),
    }));

    addEvent({
      type: "HUB_PURCHASED",
      severity: "success",
      message: `Welcome! ${hub} is now your hub.`,
    });
  }, [cash, updateWorldState, addEvent]);

  // Redirect if not Simple mode
  if (!isSimpleMode) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#000" }}>
        <div className="text-center">
          <p className="text-4xl opacity-30 mb-4">--</p>
          <h2 style={{ color: "#e5e5e5", fontSize: "20px", marginBottom: "8px" }}>Hub Store</h2>
          <p style={{ color: "#737373", marginBottom: "24px" }}>Not available in Simulation mode.</p>
          <p style={{ color: "#525252", fontSize: "13px", marginBottom: "24px" }}>
            In Simulation mode, expand your network through slot negotiations.
          </p>
          <Button
            onClick={() => navigate("/opscenter")}
            style={{
              backgroundColor: "#1a1a1a",
              color: "#a1a1a1",
              border: "1px solid #333",
            }}
          >
            Back to Ops Center
          </Button>
        </div>
      </div>
    );
  }

  // Group hubs by region
  const regions = [...new Set(SIMPLE_HUBS.map(h => h.region))];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#000" }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 flex items-center justify-between p-4"
        style={{ backgroundColor: "#0a0a0a", borderBottom: "1px solid #1a1a1a" }}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/opscenter")}
            style={{ color: "#a1a1a1" }}
          >
            &lt; {t('common.back')}
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold" style={{ color: "#fff" }}>
              {t('hubStore.title')}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Cash display */}
          <div style={{
            backgroundColor: "#0f0f0f",
            border: "1px solid #1a1a1a",
            padding: "8px 16px",
            borderRadius: "8px",
          }}>
            <span style={{ color: "#737373", fontSize: "12px", marginRight: "8px" }}>{t('hubStore.cash')}:</span>
            <span style={{ color: "#22c55e", fontWeight: "bold", fontSize: "16px" }}>
              {formatPrice(cash)}
            </span>
          </div>
        </div>
      </div>

      {/* Owned Hubs Summary */}
      <div style={{
        backgroundColor: "#0a0a0a",
        borderBottom: "1px solid #1a1a1a",
        padding: "16px 24px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <span style={{ color: "#a1a1a1", fontSize: "14px" }}>{t('hubStore.yourHubs')}:</span>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {ownedHubs.map(icao => (
            <span
              key={icao}
              style={{
                backgroundColor: "#22c55e20",
                color: "#22c55e",
                padding: "4px 12px",
                borderRadius: "4px",
                fontSize: "13px",
                fontWeight: "bold",
              }}
            >
              {icao}
            </span>
          ))}
        </div>
      </div>

      {/* Pending Exclusive Offers - Show if any exist */}
      {pendingOffers.length > 0 && (
        <div style={{
          backgroundColor: "#0d1f1a",
          borderBottom: "1px solid #22c55e40",
          padding: "16px 24px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <span style={{ color: "#22c55e", fontSize: "14px", fontWeight: "bold" }}>
              EXCLUSIVE OFFERS WAITING
            </span>
            <span style={{
              backgroundColor: "#22c55e",
              color: "#000",
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: "bold",
            }}>
              {pendingOffers.length}
            </span>
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {pendingOffers.map(offer => {
              const canAffordOffer = cash >= offer.price;
              return (
                <div
                  key={offer.hub}
                  style={{
                    backgroundColor: "#0a0a0a",
                    border: `1px solid ${canAffordOffer ? "#22c55e" : "#f59e0b"}`,
                    borderRadius: "8px",
                    padding: "12px 16px",
                    minWidth: "200px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ color: "#fff", fontWeight: "bold", fontSize: "16px" }}>{offer.hub}</span>
                    <span style={{ color: "#22c55e", fontWeight: "bold" }}>{formatPrice(offer.price)}</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#737373", marginBottom: "8px" }}>
                    Exclusive offer - no auction required
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAcceptPendingOffer(offer)}
                    disabled={!canAffordOffer}
                    style={{
                      width: "100%",
                      backgroundColor: canAffordOffer ? "#22c55e" : "#333",
                      color: canAffordOffer ? "#000" : "#666",
                      fontWeight: "bold",
                    }}
                  >
                    {canAffordOffer ? "Accept Offer" : `Need ${formatPrice(offer.price - cash)} more`}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        {/* Intro text */}
        <p style={{ color: "#737373", marginBottom: "32px", lineHeight: "1.6" }}>
          {t('hubStore.intro')}
        </p>

        {/* Hub cards grouped by region */}
        {regions.map(region => (
          <div key={region} style={{ marginBottom: "32px" }}>
            <h2 style={{
              color: "#525252",
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: "16px",
              paddingBottom: "8px",
              borderBottom: "1px solid #1a1a1a",
            }}>
              {region}
            </h2>
            <div style={{ display: "grid", gap: "16px" }}>
              {SIMPLE_HUBS
                .filter(hub => hub.region === region)
                .map(hub => (
                  <HubCard
                    key={hub.icao}
                    hub={hub}
                    isOwned={ownedHubs.includes(hub.icao)}
                    canAfford={cash >= hub.price}
                    onPurchase={handlePurchase}
                    t={t}
                  />
                ))}
            </div>
          </div>
        ))}

        {/* Footer info */}
        <div style={{
          backgroundColor: "#0a0a0a",
          border: "1px solid #1a1a1a",
          borderRadius: "8px",
          padding: "16px",
          marginTop: "32px",
        }}>
          <p style={{ color: "#525252", fontSize: "12px", lineHeight: "1.6" }}>
            <strong style={{ color: "#737373" }}>{t('hubStore.tipLabel')}:</strong> {t('hubStore.tip')}
          </p>
        </div>
      </div>
    </div>
  );
}
